import React, { useState, useEffect } from 'react';
import PaymentPopup from '../payments/PaymentPopup';
import { getPayabliTokens } from '../../../utils/apis';
import { IPaymentObject } from '../../../utils/types';
import { validatePhoneNumber, getPhoneNumberForBackend } from '../../../utils/utils';
import DonationHeader from './DonationHeader';
import DonationAmountSelector, { SelectedLevel, LEVELS } from './DonationAmountSelector';
import DonorInformation from './DonorInformation';
import { FailurePopup } from '../../shared/FailurePopup';
import Footer from '../../shared/Footer';
import DonationSuccessModal from './DonationSuccessModal';

type DonationFrequency = 'MONTHLY' | 'ANNUALLY' | 'ONETIME';

interface DonationFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  specialDesignation: string;
  amount: number;
}

interface DonationFormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  streetAddress?: string;
}

interface PayabliConfig {
  token: string;
  entry: string;
}

const FREQUENCY_DISPLAY: Record<DonationFrequency, string> = {
  MONTHLY: 'Monthly',
  ANNUALLY: 'Annually',
  ONETIME: 'One-Time',
};

const Donate: React.FC = () => {
  const [formData, setFormData] = useState<DonationFormData>({
    firstName: '', lastName: '', email: '', phoneNumber: '',
    streetAddress: '', city: '', state: '', zipCode: '',
    specialDesignation: '', amount: 0
  });
  const [frequency, setFrequency] = useState<DonationFrequency>('MONTHLY');
  const [errors, setErrors] = useState<DonationFormErrors>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [payabliConfig, setPayabliConfig] = useState<PayabliConfig | null>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [submittedFormData, setSubmittedFormData] = useState<DonationFormData | null>(null);
  const [paymentReferenceId, setPaymentReferenceId] = useState<string>('');
  const [isEmailValid, setIsEmailValid] = useState(false);

  // Selected level — when set, shows the "level detail" screen
  const [selectedLevel, setSelectedLevel] = useState<SelectedLevel | null>(null);

  // General donation custom amount
  const [customAmount, setCustomAmount] = useState<string>('');

  useEffect(() => {
    const fetchPayabliConfig = async () => {
      try {
        setIsLoadingConfig(true);
        const response = await getPayabliTokens('donation');
        setPayabliConfig({
          token: response?.data?.token,
          entry: response?.data?.entry
        });
      } catch (error) {
        console.error('Error fetching Payabli configuration:', error);
      } finally {
        setIsLoadingConfig(false);
      }
    };
    fetchPayabliConfig();
  }, []);

  const validateEmailFormat = (email: string): boolean => {
    if (!email.trim()) return false;
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!emailRegex.test(email)) return false;
    if (email.length > 254) return false;
    if (email.includes('..')) return false;
    const localPart = email.split('@')[0];
    if (localPart.startsWith('.') || localPart.endsWith('.')) return false;
    if (email.includes(' ')) return false;
    const domain = email.split('@')[1];
    const tld = domain?.split('.').pop();
    if (!tld || tld.length < 2) return false;
    return true;
  };

  const validateForm = (): boolean => {
    const newErrors: DonationFormErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmailFormat(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!validatePhoneNumber(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Phone number must have 10 digits';
    }
    if (!formData.streetAddress.trim()) {
      newErrors.streetAddress = 'Street address is required';
    }
    if (formData.amount <= 0) return false;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof DonationFormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
    if (field === 'email') {
      setIsEmailValid(validateEmailFormat(value));
    }
  };

  const handleDonateClick = () => {
    if (!validateForm()) return;
    if (!payabliConfig?.token || !payabliConfig?.entry) {
      setErrorMsg('Payment system is not configured. Please try again later.');
      return;
    }
    setIsProcessing(true);
    setShowPaymentPopup(true);
  };

  const closePaymentPopup = () => {
    setShowPaymentPopup(false);
    setIsProcessing(false);
  };

  const handlePaymentSuccess = (message: string, referenceId?: string) => {
    setSubmittedFormData({ ...formData });
    if (referenceId && referenceId.trim()) {
      setPaymentReferenceId(referenceId);
    }
    setShowSuccessPopup(true);
    closePaymentPopup();
    resetForm();
  };

  const handlePaymentError = (message: string) => {
    setErrorMsg(`Payment failed: ${message}`);
    closePaymentPopup();
  };

  const resetForm = () => {
    setFormData({
      firstName: '', lastName: '', email: '', phoneNumber: '',
      streetAddress: '', city: '', state: '', zipCode: '',
      specialDesignation: '', amount: 0
    });
    setIsEmailValid(false);
    setCustomAmount('');
  };

  const isFormValid = () => {
    return formData.firstName.trim() !== '' &&
           formData.lastName.trim() !== '' &&
           formData.email.trim() !== '' &&
           validateEmailFormat(formData.email) &&
           formData.phoneNumber.trim() !== '' &&
           validatePhoneNumber(formData.phoneNumber) &&
           formData.streetAddress.trim() !== '' &&
           formData.amount > 0;
  };

  const createPaymentObject = (): IPaymentObject => {
    const isRecurring = frequency !== 'ONETIME';
    const startDate = isRecurring
      ? new Date(Date.now() + 86400000).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0];

    return {
      amount: formData.amount,
      startDate,
      frequency: frequency,
      isRecurring,
      guarantorLastName: formData.lastName,
      guarantorNumber: `donation_${Date.now()}`,
      identityId: `donation_${Date.now()}`,
      token: payabliConfig?.token || '',
      entityId: payabliConfig?.entry || '',
      paymentType: 'DONATION',
      donationType: selectedLevel?.title || 'General Donation',
      donationData: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: getPhoneNumberForBackend(formData.phoneNumber),
        address: formData.streetAddress,
        specialDesignation: formData.specialDesignation,
      }
    };
  };

  // --- Level selection handler ---
  const handleLevelSelect = (level: SelectedLevel) => {
    setSelectedLevel(level);
    setFrequency(level.frequency);
    setFormData(prev => ({ ...prev, amount: level.amount }));
  };

  const handleBackToLevels = () => {
    setSelectedLevel(null);
    setFormData(prev => ({ ...prev, amount: 0 }));
    setErrors({});
  };

  // --- General donation handler ---
  const handleGeneralDonate = () => {
    const parsed = parseFloat(customAmount);
    if (isNaN(parsed) || parsed <= 0) return;
    setFrequency('ONETIME');
    setFormData(prev => ({ ...prev, amount: parsed }));
    setSelectedLevel({ title: 'General Donation', amount: parsed, frequency: 'ONETIME', description: '' });
  };

  if (isLoadingConfig) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#71001F] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // VIEW: Level Detail Screen (amount pre-set, fill in info)
  // ============================================================
  if (selectedLevel) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <DonationSuccessModal
          isOpen={showSuccessPopup && !!submittedFormData}
          onClose={() => {
            setShowSuccessPopup(false);
            setSubmittedFormData(null);
            setPaymentReferenceId('');
            setSelectedLevel(null);
            window.location.href = 'https://stlukehealthcare.org/donate/';
          }}
          email={submittedFormData?.email || ''}
          paymentReferenceId={paymentReferenceId}
          amount={submittedFormData?.amount || 0}
          frequency={frequency}
        />
        <FailurePopup
          message={errorMsg}
          isOpen={!!errorMsg}
          onClose={() => setErrorMsg('')}
        />
        <DonationHeader />

        <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
          {/* Back button */}
          <button
            onClick={handleBackToLevels}
            className="flex items-center gap-2 text-gray-600 hover:text-[#71001F] transition-colors text-sm font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to giving levels
          </button>

          {/* Selected level summary */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-[#71001F] rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-[#71001F]">{selectedLevel.title}</div>
                <h3 className="text-xl font-bold text-gray-800">Your Donation</h3>
              </div>
            </div>
            <div className="bg-red-50 rounded-lg p-4 flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-[#71001F]">${selectedLevel.amount.toLocaleString()}</div>
                <div className="text-sm text-gray-600">{FREQUENCY_DISPLAY[frequency]} donation</div>
              </div>
              {selectedLevel.title !== 'General Donation' && (
                <div className="flex gap-2">
                  {selectedLevel.title !== 'Heritage Partner' && (['MONTHLY', 'ANNUALLY', 'ONETIME'] as DonationFrequency[]).map((freq) => (
                    <button
                      key={freq}
                      onClick={() => {
                        setFrequency(freq);
                        const level = LEVELS.find(l => l.title === selectedLevel.title);
                        if (level) {
                          const newAmount = freq === 'MONTHLY' ? level.monthly : freq === 'ANNUALLY' ? level.annually : level.onetime;
                          setFormData(prev => ({ ...prev, amount: newAmount }));
                          setSelectedLevel(prev => prev ? { ...prev, amount: newAmount, frequency: freq } : null);
                        }
                      }}
                      className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                        frequency === freq
                          ? 'bg-[#71001F] text-white'
                          : 'border border-gray-300 text-gray-600 hover:bg-[#71001F] hover:text-white hover:border-[#71001F]'
                      }`}
                    >
                      {FREQUENCY_DISPLAY[freq]}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedLevel.description && (
              <p className="text-sm text-gray-500 mt-3 italic">{selectedLevel.description}</p>
            )}
          </div>

          <DonorInformation
            title="Donor Information"
            firstName={formData.firstName}
            lastName={formData.lastName}
            email={formData.email}
            phoneNumber={formData.phoneNumber}
            streetAddress={formData.streetAddress}
            city={formData.city}
            state={formData.state}
            zipCode={formData.zipCode}
            specialDesignation={formData.specialDesignation}
            onFieldChange={handleFieldChange}
            errors={errors}
            onValidationChange={(isValid) => {
              if (isValid !== undefined) setIsEmailValid(isValid);
            }}
          />

          <button
            type="button"
            onClick={handleDonateClick}
            disabled={!isFormValid() || isProcessing || !payabliConfig?.token || !payabliConfig?.entry}
            className="w-full bg-[#71001F] text-white font-semibold py-4 px-8 rounded-xl text-lg hover:bg-[#5a0018] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? 'Processing...' : `Complete ${FREQUENCY_DISPLAY[frequency]} Donation — $${formData.amount.toLocaleString()}`}
          </button>

          <p className="text-center text-xs text-gray-400 mt-4">
            Your info is as protected as our patients. All information is safe on our platforms and not shared with third parties.
          </p>
        </div>

        {showPaymentPopup && (
          <PaymentPopup
            closePopup={closePaymentPopup}
            paymentObject={createPaymentObject()}
            onSuccess={handlePaymentSuccess}
            onError={handlePaymentError}
            paymentType="DONATION"
          />
        )}

        <Footer />
      </div>
    );
  }

  // ============================================================
  // VIEW: Main Page (Legacy Campaign + General Donation)
  // ============================================================
  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <DonationSuccessModal
        isOpen={showSuccessPopup && !!submittedFormData}
        onClose={() => {
          setShowSuccessPopup(false);
          setSubmittedFormData(null);
          setPaymentReferenceId('');
          window.location.href = 'https://stlukehealthcare.org/donate/';
        }}
        email={submittedFormData?.email || ''}
        paymentReferenceId={paymentReferenceId}
        amount={submittedFormData?.amount || 0}
        frequency={frequency}
      />
      <FailurePopup
        message={errorMsg}
        isOpen={!!errorMsg}
        onClose={() => setErrorMsg('')}
      />
      <DonationHeader />

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        {/* Legacy Campaign Section — Level Cards */}
        <DonationAmountSelector onLevelSelect={handleLevelSelect} />

        {/* General Donation Section */}
        <div id="donate-form" className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100 w-full">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 bg-[#71001F] rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800">General Donation</h3>
          </div>
          <p className="text-sm text-gray-500 mb-6 ml-11">Make a one-time donation of any amount</p>

          <div className="flex gap-3">
            <div className="relative flex-1">
              <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">$</span>
              <input
                type="text"
                inputMode="decimal"
                value={customAmount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!/^\d*\.?\d{0,2}$/.test(value)) return;
                  setCustomAmount(value);
                }}
                placeholder="Enter amount"
                className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#71001F] focus:border-[#71001F] transition-all text-lg font-medium text-gray-700 bg-white"
              />
            </div>
            <button
              onClick={handleGeneralDonate}
              disabled={!customAmount || parseFloat(customAmount) <= 0}
              className="px-8 py-3 bg-[#71001F] text-white font-semibold rounded-lg hover:bg-[#5a0018] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              Donate
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400">
          Your info is as protected as our patients. All information is safe on our platforms and not shared with third parties.
        </p>
      </div>

      <Footer />
    </div>
  );
};

export default Donate;
